import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@exhousing/design-system";
import {
  Building2,
  CalendarCheck,
  ClipboardCheck,
  Inbox,
} from "lucide-react";
import { Link } from "react-router-dom";

import { StatusBadge } from "@/components/StatusBadge";
import { useContracts } from "@/hooks/api/useContracts";
import { useApplications } from "@/hooks/api/useApplications";
import { useInspections } from "@/hooks/api/useInspections";
import { useViewings } from "@/hooks/api/useViewings";
import { useMe } from "@/hooks/api/useAuth";
import { isLandlord } from "@/lib/roles";

const TYPE_LABELS: Record<string, string> = {
  check_in: "Check-in",
  check_out: "Check-out",
  interim: "Interim",
};

/** Threads everything tied to one property: applications, viewings, contracts, inspections. */
export function PropertyLifecycleCard({ propertyId }: { propertyId: string }) {
  const { data: me } = useMe();
  const landlordView = isLandlord(me);
  const { data: contracts } = useContracts();
  // Landlord guests have no viewing/inspection permissions — don't even ask.
  const { data: inspections } = useInspections(undefined, { enabled: !landlordView });
  const { data: viewings } = useViewings({ enabled: !landlordView });
  const { data: applications } = useApplications({ propertyId });

  const myContracts = (contracts ?? []).filter((c) => c.property_id === propertyId);
  const myInspections = (inspections ?? []).filter((i) => i.property_id === propertyId);
  const myViewings = (viewings ?? []).filter((v) => v.property_id === propertyId);
  const myApplications = applications ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Lifecycle</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Link
            to={`/applications?property=${propertyId}`}
            className="rounded-lg border border-border p-3 transition-colors hover:border-primary"
          >
            <Inbox className="size-4 text-muted-foreground" />
            <p className="mt-2 font-display text-2xl font-medium tabular-nums">
              {myApplications.length}
            </p>
            <p className="text-xs text-muted-foreground">Applications</p>
          </Link>
          {!landlordView && (
            <Link
              to={`/viewings?property=${propertyId}`}
              className="rounded-lg border border-border p-3 transition-colors hover:border-primary"
            >
              <CalendarCheck className="size-4 text-muted-foreground" />
              <p className="mt-2 font-display text-2xl font-medium tabular-nums">
                {myViewings.length}
              </p>
              <p className="text-xs text-muted-foreground">Viewings</p>
            </Link>
          )}
          <div className="rounded-lg border border-border p-3">
            <Building2 className="size-4 text-muted-foreground" />
            <p className="mt-2 font-display text-2xl font-medium tabular-nums">
              {myContracts.length}
            </p>
            <p className="text-xs text-muted-foreground">Contracts</p>
          </div>
          {!landlordView && (
            <div className="rounded-lg border border-border p-3">
              <ClipboardCheck className="size-4 text-muted-foreground" />
              <p className="mt-2 font-display text-2xl font-medium tabular-nums">
                {myInspections.length}
              </p>
              <p className="text-xs text-muted-foreground">Inspections</p>
            </div>
          )}
        </div>

        {myContracts.length > 0 && (
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Contracts
            </p>
            <ul className="divide-y divide-border/60">
              {myContracts.map((c) => (
                <li key={c.id} className="flex items-center justify-between py-2 text-sm">
                  <Link
                    to={`/contracts/${c.id}`}
                    className="font-medium hover:text-primary"
                  >
                    {c.tenants ?? c.reference ?? "Contract"}
                    {c.start_date ? (
                      <span className="text-muted-foreground"> · from {c.start_date}</span>
                    ) : null}
                  </Link>
                  <StatusBadge status={c.status} />
                </li>
              ))}
            </ul>
          </div>
        )}

        {myInspections.length > 0 && (
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Inspections
            </p>
            <ul className="divide-y divide-border/60">
              {myInspections.map((i) => (
                <li key={i.id} className="flex items-center justify-between py-2 text-sm">
                  <Link
                    to={`/inspections/${i.id}`}
                    className="font-medium hover:text-primary"
                  >
                    {TYPE_LABELS[i.type] ?? i.type}
                    {i.performed_at ? (
                      <span className="text-muted-foreground"> · {i.performed_at}</span>
                    ) : null}
                  </Link>
                  <StatusBadge status={i.status} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
